// The department steps of a production (Masterplan, "Productieproces"): the nine core ones plus the extras the
// production added, the gate in front of the finals, and the sign-off rules per department. The steps, their rules and the sign-offs live on the server (F4,
// GET /episodes/{id}/steps); `deriveSteps` turns that answer plus what the data says (script versions, shots,
// picks) into the lines of the strip. The rule helpers mirror the server's (role mode: that role or higher). Pure.
import {
  byProductionOrder,
  DEPARTMENT_LABEL,
  type Department,
  isExtra,
  PRODUCTION_ORDER,
} from "../departments.ts";
import type { ItemAssignment } from "./itemCrew.ts";

/**
 * Square states (BRAND.md "The strip of squares") plus "progress" (work started, nobody asked for sign-off yet)
 * and "skipped" (the production does not run this step).
 */
export type StepState = "signed" | "waiting" | "progress" | "open" | "gated" | "skipped";

export interface DeptStep {
  dept: Department;
  state: StepState;
  /** a plain line under the step: "Signed off", "Waiting for you", "3 of 14 shots", "Opens after the animatic" */
  label: string;
  /** the longer line for the tooltip, when the label is a summary ("Waits on 6 sign-offs": which ones) */
  hint?: string;
  /** a still of the signed-off work, to fill the square */
  frame?: string | null;
}

export const STATE_TEXT: Record<StepState, string> = {
  signed: "Signed off",
  waiting: "Waiting for sign-off",
  progress: "In progress",
  open: "Open",
  gated: "Gated",
  skipped: "Skipped",
};

/** A request for sign-off, or a request for changes: who (a name), when, and the note. */
export interface StepWord {
  by: string | null;
  at: string;
  note: string | null;
}

/** "Changes requested by you just now", "Changes requested by Anna 5 min ago" (`ago` is the time as text). */
export function changesRequestedLine(w: Pick<StepWord, "by">, ago: string): string {
  return `Changes requested${w.by ? ` by ${w.by}` : ""} ${ago}`;
}

/** What the steps source knows about one department of one episode (the server's EpisodeStep, with names). */
export interface DeptSignoff {
  state: "signed" | "waiting" | "changes" | "open" | "skipped";
  /** names of the people the step still waits on */
  waitingOn?: string[];
  /** the current user is one of them */
  waitingOnMe?: boolean;
  /** the current user may sign this step off */
  canSign?: boolean;
  /** names of who signed off in the current round */
  signedBy?: string[];
  /** the open request for sign-off */
  request?: StepWord | null;
  /** the last request for changes, when it is the newest word */
  changes?: StepWord | null;
  /** the last sign-off went stale at (something changed after it), when that is the newest word */
  staleSince?: string | null;
  /** the step's version token: the last change the sign-off is about (sent back when signing off) */
  version?: string | null;
  /** the step's rule (who signs off, how), for "who will be asked" */
  rule?: SignoffRule;
  frame?: string | null;
}

/** The finals gate as the server answers it (ff.finals_gate). */
export interface ServerGate {
  open: boolean;
  waitingOn: Department[];
}

/** What the real data of one episode says, counted. */
export interface StepFacts {
  scriptRevisions: number;
  /**
   * Writing's own sheets of the episode (synopsis, treatment, tone; production-wide ones too), counted by the server
   * (GET /episodes/{id}/writing-sheets). They start Writing only while no later step has started.
   */
  writingSheets?: number;
  shots: number;
  characters: number;
  locations: number;
  props: number;
  /** shots with a picked keyframe: the stills of the animatic */
  keyframesPicked: number;
  draftsPicked: number;
  finalsPicked: number;
  /** shots with a picked sound effect (the Sound department) */
  soundPicked?: number;
  /** scenes of the episode, and those with picked music (the Music department; known on the board) */
  scenes?: number;
  musicScenes?: number;
  /** a current (not stale) animatic approval on the episode */
  animaticSignedOff: boolean;
  /** the episode's review status asks for review of the animatic */
  animaticWaiting?: boolean;
  /** frames to fill signed-off squares with */
  frames?: Partial<Record<Department, string | null>>;
}

export const NO_FACTS: StepFacts = {
  scriptRevisions: 0,
  shots: 0,
  characters: 0,
  locations: 0,
  props: 0,
  keyframesPicked: 0,
  draftsPicked: 0,
  finalsPicked: 0,
  animaticSignedOff: false,
};

const of = (n: number, total: number, what: string) => `${n} of ${total} ${what}`;

const WAITING_FOR_YOU = "Waiting for you";

function waitingText(s: DeptSignoff): string {
  if (s.waitingOnMe) return WAITING_FOR_YOU;
  if (s.waitingOn?.length) return `Waiting for ${listNames(s.waitingOn)}`;
  return "Waiting for sign-off";
}

/** The step label BRAND uses for a sign-off that went stale. */
export const CHANGED_AFTER = "Changed after sign-off";

/** The plain line for a sign-off that went stale: "Script v4 came in after the sign-off". */
export function staleLine(dept: Department, facts: Pick<StepFacts, "scriptRevisions">): string {
  switch (dept) {
    case "writing":
      return facts.scriptRevisions > 0
        ? `Script v${facts.scriptRevisions} came in after the sign-off.`
        : "A new script came in after the sign-off.";
    case "breakdown":
      return "The script changed after the sign-off.";
    case "casting":
    case "wardrobe":
      return "A new cast version came in after the sign-off.";
    case "sets":
      return "A new set version came in after the sign-off.";
    case "props":
      return "A new prop version came in after the sign-off.";
    case "animatic":
      return "A new pick came in after the sign-off.";
    case "draft":
      return "A new draft was picked after the sign-off.";
    case "voice":
      return "A new character version or dialogue take came in after the sign-off.";
    case "music":
      return "New music was picked after the sign-off.";
    case "sound":
      return "A new sound was picked after the sign-off.";
    case "edit":
      return "A new pick came in after the sign-off.";
    default:
      return "Something changed after the sign-off.";
  }
}

/** Is this the server's 409 changed_since (the step moved after the person opened it)? */
export const isChangedSince = (e: unknown): boolean =>
  !!e && typeof e === "object" && "code" in e && (e as { code: unknown }).code === "changed_since";

const CHANGED_SINCE: Record<Department, string> = {
  writing: "A new script came in since you opened this.",
  breakdown: "The breakdown changed since you opened this.",
  casting: "A new cast version came in since you opened this.",
  wardrobe: "A new costume came in since you opened this.",
  sets: "A new set version came in since you opened this.",
  props: "A new prop version came in since you opened this.",
  animatic: "A new still was picked since you opened this.",
  draft: "A new draft was picked since you opened this.",
  final: "A new final was picked since you opened this.",
  voice: "A new voice or dialogue take came in since you opened this.",
  music: "New music was picked since you opened this.",
  sound: "A new sound was picked since you opened this.",
  edit: "A new pick came in since you opened this.",
};

/** The calm line after a 409 changed_since: what moved, and what to do. Nothing was signed off. */
export function changedSinceText(dept?: Department | null): string {
  return `${dept ? CHANGED_SINCE[dept] : "Something changed since you opened this."} Have a look, then sign off.`;
}

/** "Anna", "Anna and Sam", "Anna, Sam and Joe". */
export function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The departments a production runs, in production order: the nine core ones always (a skipped one too, it shows
 * as skipped), plus every extra the steps source answered for (or `extras` names) that is not skipped.
 */
export function productionDepts(
  signoffs: Partial<Record<Department, { state: string }>> = {},
  extras: readonly Department[] = [],
): Department[] {
  return PRODUCTION_ORDER.filter((d) => {
    if (!isExtra(d)) return true;
    const s = signoffs[d];
    return s ? s.state !== "skipped" : extras.includes(d);
  });
}

/**
 * The steps of an episode: the nine core ones and the extras the production runs (see `productionDepts`). The
 * sign-off source gives signed off, waiting, changes requested, skipped and stale; facts give the open / in
 * progress lines (and, without a source answer, the animatic approval). The server's gate, when it is known,
 * decides whether the final is gated.
 */
export function deriveSteps(
  facts: StepFacts,
  signoffs: Partial<Record<Department, DeptSignoff>> = {},
  opts: { locks?: readonly Department[]; gate?: ServerGate | null; extras?: readonly Department[] } = {},
): DeptStep[] {
  const started: Record<Department, string | null> = {
    writing: facts.scriptRevisions > 0 ? `Script v${facts.scriptRevisions}` : null,
    breakdown: facts.shots > 0 ? `${facts.shots} ${facts.shots === 1 ? "shot" : "shots"}` : null,
    casting: facts.characters > 0 ? count(facts.characters, "character") : null,
    wardrobe: null,
    sets: facts.locations > 0 ? count(facts.locations, "set") : null,
    props: facts.props > 0 ? count(facts.props, "prop") : null,
    animatic: facts.keyframesPicked > 0 ? of(facts.keyframesPicked, facts.shots, "stills") : null,
    draft: facts.draftsPicked > 0 ? of(facts.draftsPicked, facts.shots, "shots") : null,
    final: facts.finalsPicked > 0 ? of(facts.finalsPicked, facts.shots, "finals") : null,
    voice: null,
    music: facts.musicScenes ? of(facts.musicScenes, facts.scenes ?? facts.musicScenes, "scenes") : null,
    sound: facts.soundPicked ? of(facts.soundPicked, facts.shots, "shots") : null,
    edit: facts.finalsPicked > 0 ? of(facts.finalsPicked, facts.shots, "finals in the cut") : null,
  };
  // Without a script, Writing's own sheets say it started, but only while nothing after it has: a production-wide
  // synopsis must not pull an episode that is already in breakdown or drafting back to Writing.
  const laterStarted = PRODUCTION_ORDER.some(
    (d) =>
      d !== "writing" &&
      (started[d] !== null || ["signed", "waiting", "changes"].includes(signoffs[d]?.state ?? "")),
  );
  if (!started.writing && facts.writingSheets && !laterStarted)
    started.writing = count(facts.writingSheets, "sheet");
  const animatic: DeptSignoff | undefined =
    signoffs.animatic ??
    (facts.animaticSignedOff
      ? { state: "signed", frame: facts.frames?.animatic ?? null }
      : facts.animaticWaiting
        ? { state: "waiting" }
        : undefined);
  const all = { ...signoffs, ...(animatic ? { animatic } : {}) };
  const gate = opts.gate
    ? gateText(opts.gate.waitingOn)
    : finalsGate({ animaticSignedOff: animatic?.state === "signed", signoffs: all, locks: opts.locks });

  return productionDepts(all, opts.extras).map((id): DeptStep => {
    const s = all[id];
    const frame = s?.frame ?? facts.frames?.[id] ?? null;
    if (s?.state === "skipped") return { dept: id, state: "skipped", label: "Skipped" };
    if (s?.state === "signed") return { dept: id, state: "signed", label: "Signed off", frame };
    if (s?.state === "waiting") return { dept: id, state: "waiting", label: waitingText(s) };
    if (id === "final" && !gate.open)
      return { dept: id, state: "gated", label: gate.short, hint: gate.reason };
    if (s?.state === "changes") return { dept: id, state: "progress", label: "Changes requested" };
    if (s?.staleSince) return { dept: id, state: "progress", label: CHANGED_AFTER };
    const line = started[id];
    return line ? { dept: id, state: "progress", label: line } : { dept: id, state: "open", label: "Open" };
  });
}

const count = (n: number, what: string) => `${n} ${n === 1 ? what : `${what}s`}`;

export interface Gate {
  open: boolean;
  /** the sentence for the gate (BRAND.md: never "Locked") */
  reason: string;
  /** a short line for a step label */
  short: string;
  /** departments that still have to sign off */
  waitingOn: Department[];
}

/**
 * "Finals only after the animatic is signed off" (Masterplan rule 3), plus every department whose rule locks
 * the finals until it is signed off (p2-crew, "Finals wait for this sign-off"). Drafts never wait.
 */
export function finalsGate(input: {
  animaticSignedOff: boolean;
  signoffs?: Partial<Record<Department, DeptSignoff>>;
  locks?: readonly Department[];
}): Gate {
  const waiting: Department[] = [];
  if (!input.animaticSignedOff) waiting.push("animatic");
  for (const d of input.locks ?? []) {
    if (d === "final" || d === "animatic") continue;
    if (input.signoffs?.[d]?.state !== "signed") waiting.push(d);
  }
  return gateText(waiting);
}

/** The gate's sentences for the departments it still waits on (the server's `waiting_on`, or the client's). */
export function gateText(waitingOn: readonly Department[]): Gate {
  const waiting = [...new Set(waitingOn)].sort(byProductionOrder);
  if (waiting.length === 0)
    return { open: true, reason: "Ready to render finals.", short: "Ready", waitingOn: [] };
  const names = listNames(waiting.map((d) => DEPARTMENT_LABEL[d].toLowerCase()));
  const verb = waiting.length === 1 ? "is" : "are";
  return {
    open: false,
    reason: `Finals render once the ${names} ${verb} signed off.`,
    // one department fits the step cell by name; more are counted, the names go in the tooltip
    short: waiting.length === 1 ? `Opens after ${names}` : `Waits on ${waiting.length} sign-offs`,
    waitingOn: waiting,
  };
}

/** "4 of 9 done" (a skipped step does not count). */
export function doneText(steps: readonly DeptStep[]): string {
  const run = steps.filter((s) => s.state !== "skipped");
  return `${run.filter((s) => s.state === "signed").length} of ${run.length} done`;
}

/** Departments whose name reads as a plural ("Sets are waiting", "Props are next"). */
export const PLURAL_DEPTS: ReadonlySet<Department> = new Set(["sets", "props", "voice"]);

/**
 * What waits, grouped by who it waits on, yours first: "Casting and sets waiting for you, wardrobe for Sam". Two
 * groups get names and the rest is counted, so the line stays short; the strip above it shows which steps those are.
 */
function waitingLine(waiting: readonly DeptStep[]): string {
  const byWho = new Map<string, Department[]>();
  for (const s of waiting) byWho.set(s.label, [...(byWho.get(s.label) ?? []), s.dept]);
  const groups = [...byWho].sort(([a], [b]) => Number(b === WAITING_FOR_YOU) - Number(a === WAITING_FOR_YOU));
  const named = groups.slice(0, 2).map(([label, depts], g) => {
    const names = listNames(depts.map((d) => DEPARTMENT_LABEL[d].toLowerCase()));
    return `${names} ${label.replace(/^Waiting /, g === 0 ? "waiting " : "")}`;
  });
  const more = groups.slice(2).reduce((n, [, depts]) => n + depts.length, 0);
  const line = [...named, ...(more ? [`${more} more waiting`] : [])].join(", ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** The one line under a production card: what waits, on whom ("Sets and props waiting for you"). */
export function stripLine(steps: readonly DeptStep[]): string {
  const waiting = steps.filter((s) => s.state === "waiting");
  if (waiting.length) return waitingLine(waiting);
  if (steps.every((s) => s.state === "signed" || s.state === "skipped")) return "That's a wrap";
  const next = steps.find((s) => s.state === "progress") ?? steps.find((s) => s.state === "open");
  if (!next) return "Nothing started yet";
  return next.state === "progress"
    ? `${DEPARTMENT_LABEL[next.dept]} in progress`
    : `${DEPARTMENT_LABEL[next.dept]} ${PLURAL_DEPTS.has(next.dept) ? "are" : "is"} next`;
}

/**
 * The line under the home hero when nothing waits for you. The headline already names the next step ("Breakdown
 * is next"), so this says what signing it off opens, or who something waits on, never the next step again.
 */
export function heroLine(steps: readonly DeptStep[], focus: DeptStep | null): string {
  const strip = stripLine(steps);
  if (steps.some((s) => s.state === "waiting")) return strip;
  if (!focus) return strip;
  const run = steps.filter((s) => s.state !== "skipped");
  const after = run.slice(run.findIndex((s) => s.dept === focus.dept) + 1).find((s) => s.state !== "signed");
  const plural = PLURAL_DEPTS.has(focus.dept);
  const lead = focus.state === "progress" ? "Under way. " : "";
  if (!after) return `${lead}Sign ${plural ? "them" : "it"} off to wrap the episode.`;
  const next = DEPARTMENT_LABEL[after.dept].toLowerCase();
  return `${lead}Once ${plural ? "they are" : "it is"} signed off, ${next} can start.`;
}

// ── sign-off rules ────────────────────────────────────────────────────────────────────────────────────────

/** p2-crew "Moves on when": one of the signers, everyone of them, or whoever has a role. */
export type RuleMode = "one_of" | "everyone" | "role";

export interface SignoffRule {
  dept: Department;
  /** user ids that do the work */
  workers: string[];
  /** user ids that may sign off (one_of, everyone); empty: a director or higher signs off */
  signers: string[];
  mode: RuleMode;
  /** the role that signs off (mode "role"): whoever has that role or a higher one */
  role?: string;
  /** nobody renders finals until this department is signed off */
  locksFinals: boolean;
  /** the production does not run this step */
  skipped?: boolean;
  /** people on single items of the step (a set, a shot...); only when there are any */
  items?: ItemAssignment[];
}

/** The rows of the rules table: every step but an extra that was taken out of the flow (it can be added again). */
export const ruleRows = (rules: readonly SignoffRule[]): SignoffRule[] =>
  rules.filter((r) => !(r.skipped && isExtra(r.dept)));

/** Workspace roles, low to high (the server's ROLE_RANK). */
const RANK: Record<string, number> = {
  viewer: 10,
  reviewer: 20,
  maker: 30,
  director: 40,
  producer: 50,
  owner: 60,
};

/** Has `role` at least the rank of `min`? */
export const roleAtLeast = (role: string | null | undefined, min: string): boolean =>
  !!role && (RANK[role] ?? 0) >= (RANK[min] ?? Number.POSITIVE_INFINITY);

/** The role a rule falls back on: its own (mode "role"), or director when it lists nobody. */
export const ruleRole = (rule: Pick<SignoffRule, "mode" | "role" | "signers">): string | null =>
  rule.mode === "role" ? (rule.role ?? "director") : rule.signers.length === 0 ? "director" : null;

/** Everyone who may sign a rule off: the listed signers, or everyone with the role or higher. */
export function signersOf(rule: SignoffRule, people: readonly Person[]): string[] {
  const role = ruleRole(rule);
  return role ? people.filter((p) => roleAtLeast(p.role, role)).map((p) => p.id) : rule.signers;
}

export interface Person {
  id: string;
  name: string;
  role?: string | null;
}

export interface RuleResult {
  done: boolean;
  /** ids that signed off and count for the rule */
  signed: string[];
  /** ids the step still waits on (for "role": everyone with the role, any one of them will do) */
  waitingOn: string[];
}

/**
 * Does a set of sign-offs (user ids in the current round) satisfy the rule? Mirrors the server (ff.step_signed):
 * mode "role" and a rule without signers are met by anyone with that role (director by default) or higher.
 */
export function evaluateRule(
  rule: SignoffRule,
  signedBy: readonly string[],
  people: readonly Person[] = [],
): RuleResult {
  const has = new Set(signedBy);
  if (ruleRole(rule)) {
    const holders = signersOf(rule, people);
    const signed = holders.filter((id) => has.has(id));
    return { done: signed.length > 0, signed, waitingOn: signed.length > 0 ? [] : holders };
  }
  const signed = rule.signers.filter((id) => has.has(id));
  const missing = rule.signers.filter((id) => !has.has(id));
  if (rule.mode === "one_of")
    return { done: signed.length > 0, signed, waitingOn: signed.length > 0 ? [] : missing };
  return { done: missing.length === 0, signed, waitingOn: missing };
}

const A_ROLE: Record<string, string> = {
  owner: "The owner",
  producer: "A producer or the owner",
  director: "A director or higher",
  maker: "A maker or higher",
  reviewer: "A reviewer or higher",
};

/** The plain sentence under a rule: "Sam or Dream signs off", "A director or higher signs off". */
export function ruleText(rule: SignoffRule, nameOf: (id: string) => string): string {
  if (rule.skipped) return "Skipped in this production";
  const role = ruleRole(rule);
  if (role) return `${A_ROLE[role] ?? `Whoever is ${role}`} signs off`;
  const names = rule.signers.map(nameOf);
  if (names.length === 1) return `${names[0]} signs off`;
  if (rule.mode === "one_of")
    return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]} signs off`;
  return names.length === 2
    ? `${names[0]} and ${names[1]} both sign off`
    : `${listNames(names)} all sign off`;
}
